import type { DayCell as DayCellPayload, HoursParsed } from '~/lib/api/types'
import { BackfillButton } from './backfill-button'
import styles from './day-cell.module.scss'

type Props = {
  cell: DayCellPayload
  date: string
  todayIso: string
}

export function DayCell({ cell, date, todayIso }: Props) {
  switch (cell.kind) {
    case 'working':
      return <WorkingCell cell={cell} />
    case 'parse_failed':
      return <ParseFailedCell cell={cell} />
    case 'ooo':
      return <OooCell cell={cell} />
    case 'missing':
      return <MissingCell cell={cell} date={date} todayIso={todayIso} />
    default:
      return null
  }
}

function WorkingCell({
  cell,
}: {
  cell: Extract<DayCellPayload, { kind: 'working' }>
}) {
  const hoursText = formatHoursText(cell.hours_parsed)
  return (
    <div
      className={styles.cell}
      aria-label={cell.is_private ? 'working (private)' : 'working'}
    >
      {hoursText && (
        <p className={`${styles.hours} mono`}>
          {cell.is_private && (
            <span className={styles.lock} aria-label="private">
              🔒
            </span>
          )}
          {hoursText}
        </p>
      )}
    </div>
  )
}

function ParseFailedCell({
  cell,
}: {
  cell: Extract<DayCellPayload, { kind: 'parse_failed' }>
}) {
  return (
    <div
      className={styles.cell}
      aria-label={cell.is_private ? 'parse failed (private)' : 'parse failed'}
    >
      {cell.hours_raw && (
        <p className={`${styles.hoursRaw} mono`}>
          {cell.is_private && (
            <span className={styles.lock} aria-label="private">
              🔒
            </span>
          )}
          {cell.hours_raw}
        </p>
      )}
      <a className={styles.fixParse} href={`#fix-parse-${cell.checkin_id}`}>
        fix parse →
      </a>
    </div>
  )
}

function OooCell({ cell }: { cell: Extract<DayCellPayload, { kind: 'ooo' }> }) {
  // Single-day rendering for Task 4. Task 5 collapses contiguous days into
  // a single labeled band that spans columns.
  return (
    <div
      className={styles.oooCell}
      aria-label={`out of office (${cell.ooo_kind})`}
    >
      <span
        className={`${styles.oooShape} ${shapeClass(cell.ooo_kind)}`}
        aria-hidden
      />
      <span className={styles.oooKind}>{cell.ooo_kind}</span>
    </div>
  )
}

function MissingCell({
  cell,
  date,
  todayIso,
}: {
  cell: Extract<DayCellPayload, { kind: 'missing' }>
  date: string
  todayIso: string
}) {
  const cmp = compareIso(date, todayIso)
  if (cmp > 0) {
    // Future, nothing scheduled. The cleanest state.
    return <div className={styles.cellEmpty} aria-label="nothing scheduled" />
  }
  if (cmp === 0) {
    return (
      <div className={styles.missingCell} aria-label="missing today">
        <span className={styles.missingDot} aria-hidden />
        <span className={styles.missingLabel}>missing</span>
      </div>
    )
  }
  const days = daysAgo(date, todayIso)
  return (
    <div
      className={styles.missingCell}
      aria-label={`missing ${days} day${days === 1 ? '' : 's'}`}
    >
      <span className={styles.missingDot} aria-hidden />
      <span className={styles.missingLabel}>missing {days}d</span>
      <BackfillButton userId={cell.user_id} date={date} />
    </div>
  )
}

function formatHoursText(parsed: HoursParsed): string {
  if (!parsed) return ''
  let segs: { start: string; end: string }[] = []
  if (parsed.segments && parsed.segments.length > 0) {
    segs = parsed.segments
  } else if (parsed.start && parsed.end) {
    segs = [{ start: parsed.start, end: parsed.end }]
  }
  return segs.map(s => `${shortHour(s.start)}–${shortHour(s.end)}`).join(', ')
}

function shortHour(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':')
  const h = Number(hStr)
  const m = Number(mStr ?? '0')
  const suffix = h < 12 ? 'a' : 'p'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return m === 0
    ? `${h12}${suffix}`
    : `${h12}:${String(m).padStart(2, '0')}${suffix}`
}

function shapeClass(kind: 'vacation' | 'sick' | 'holiday' | 'other'): string {
  return styles[`shape_${kind}`] ?? ''
}

function compareIso(a: string, b: string): number {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

function daysAgo(pastIso: string, todayIso: string): number {
  const [py, pm, pd] = pastIso.split('-').map(Number)
  const [ty, tm, td] = todayIso.split('-').map(Number)
  const past = new Date(py as number, (pm as number) - 1, pd as number)
  const today = new Date(ty as number, (tm as number) - 1, td as number)
  return Math.round((today.getTime() - past.getTime()) / (24 * 60 * 60 * 1000))
}
